import { useAppStore } from '../../../store/useAppStore';
import { logger } from '../../../utils/logger';
import {
    loadClassifiedStructure,
    loadExtractedProps,
    loadExtractedSoundCues,
    loadScriptBlocks,
} from '../../sync/script-storage-api';
import type { ProjectScope } from '../../sync/project-scope';
import { mergeExtractedProps, mergeExtractedSoundCues } from './mergeImportedProductionElements';

interface ApplyScriptImportResultsOptions {
    importMode: 'replace' | 'append';
    idPrefix: string;
    includeScriptBlocks?: boolean;
    skipProductionElementMerge?: boolean;
    shouldApply?: () => boolean;
}

function mapAppendedSceneId(idPrefix: string, sceneId: string): string {
    return `scene-${idPrefix}-${sceneId}`;
}

export async function applyScriptImportResults(
    scope: ProjectScope,
    options: ApplyScriptImportResultsOptions
): Promise<boolean> {
    const includeScriptBlocks = options.includeScriptBlocks ?? true;
    const includeProductionElements = !options.skipProductionElementMerge;
    const [blocksResult, classifiedStructureResult, propsResult, soundCuesResult] = await Promise.allSettled([
        includeScriptBlocks ? loadScriptBlocks(scope) : Promise.resolve([]),
        loadClassifiedStructure(scope),
        includeProductionElements ? loadExtractedProps(scope, { jobId: options.idPrefix }) : Promise.resolve([]),
        includeProductionElements ? loadExtractedSoundCues(scope, { jobId: options.idPrefix }) : Promise.resolve([]),
    ]);

    if (options.shouldApply && !options.shouldApply()) {
        return false;
    }

    const blocks = blocksResult.status === 'fulfilled' ? blocksResult.value : [];
    const classifiedStructure = classifiedStructureResult.status === 'fulfilled'
        ? classifiedStructureResult.value
        : null;
    const extractedProps = propsResult.status === 'fulfilled' ? propsResult.value : [];
    const extractedSoundCues = soundCuesResult.status === 'fulfilled' ? soundCuesResult.value : [];

    if (blocksResult.status === 'rejected') {
        logger.warn('No script blocks loaded for completed import:', blocksResult.reason);
    }
    if (classifiedStructureResult.status === 'rejected') {
        logger.warn('No classified structure loaded for completed import:', classifiedStructureResult.reason);
    }
    if (propsResult.status === 'rejected') {
        logger.warn('No extracted props loaded for completed import:', propsResult.reason);
    }
    if (soundCuesResult.status === 'rejected') {
        logger.warn('No extracted sound cues loaded for completed import:', soundCuesResult.reason);
    }

    const showStructureActions = useAppStore.getState().showStructureActions;

    if (options.importMode === 'append') {
        showStructureActions.appendImportedStructure({
            acts: classifiedStructure?.acts || [],
            scenes: (classifiedStructure?.scenes || []).map(scene => ({
                ...scene,
                linkedSetId: null,
                notes: scene.notes || '',
            })),
            characters: classifiedStructure?.characters || [],
            scriptBlocks: blocks,
        }, { idPrefix: options.idPrefix });

        const mapSceneId = (sceneId: string) => mapAppendedSceneId(options.idPrefix, sceneId);
        mergeExtractedProps(extractedProps, { mapSceneId });
        mergeExtractedSoundCues(extractedSoundCues, { mapSceneId });
        return true;
    }

    const hasReplacementBlocks = blocks.length > 0;
    if (hasReplacementBlocks) {
        showStructureActions.setScriptBlocks(blocks);
    } else if (includeScriptBlocks) {
        logger.warn('No replacement script blocks loaded for completed import; preserving existing script blocks.');
    }

    if (classifiedStructure) {
        showStructureActions.applyClassifiedStructure(classifiedStructure);
    } else if (hasReplacementBlocks) {
        showStructureActions.deriveStructureFromBlocks();
        showStructureActions.deriveCharactersFromBlocks();
    }

    mergeExtractedProps(extractedProps);
    mergeExtractedSoundCues(extractedSoundCues);
    return true;
}
